import React, { useEffect, useState, useMemo } from 'react';
import { fetchParcelsList, type Parcel, type Filters } from '../lib/api';
import { designTokens, getScoreColor, getScoreTier, getDCScoreColor } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { formatDcScore, pipelineReferencePresentation } from '../lib/pipeline-reference';

type SortField = 'deal_quality' | 'acquisition_score' | 'dc_suitability_score' | 'acreage' | 'total_appraised';
type SortDir = 'asc' | 'desc';

const dealQuality = (p: Parcel): number =>
  Math.round(0.65 * (p.dc_suitability_score || 0) + 0.35 * (p.acquisition_score || 0));

const getDealColor = (score: number): string => {
  if (score >= 70) return designTokens.colors.dcPrime;
  if (score >= 50) return designTokens.colors.dcStrong;
  if (score >= 30) return designTokens.colors.dcViable;
  return designTokens.colors.dcWeak;
};

const getDealTier = (score: number): string => {
  if (score >= 70) return 'A';
  if (score >= 50) return 'B';
  if (score >= 30) return 'C';
  return 'D';
};

interface ListViewProps {
  filters: Filters;
  onSelectParcel: (id: string) => void;
}

export const ListView: React.FC<ListViewProps> = ({ filters, onSelectParcel }) => {
  const { theme } = useTheme();
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('deal_quality');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    setLoading(true);
    fetchParcelsList(filters, 200)
      .then(data => { setParcels(data.parcels); setHasMore(data.has_more); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters]);

  const sorted = useMemo(() => {
    const list = [...parcels];
    const dir = sortDir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      let va: number, vb: number;
      switch (sortField) {
        case 'deal_quality':
          va = dealQuality(a); vb = dealQuality(b); break;
        case 'acquisition_score':
          va = a.acquisition_score || 0; vb = b.acquisition_score || 0; break;
        case 'dc_suitability_score':
          va = a.dc_suitability_score || 0; vb = b.dc_suitability_score || 0; break;
        case 'acreage':
          va = a.acreage || 0; vb = b.acreage || 0; break;
        case 'total_appraised':
          va = Number(a.total_appraised) || 0; vb = Number(b.total_appraised) || 0; break;
        default:
          va = 0; vb = 0;
      }
      return (va - vb) * dir;
    });
    return list;
  }, [parcels, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'desc' ? ' \u25BE' : ' \u25B4') : '';

  const colCount = 8;

  return (
    <div style={{ flex: 1, overflow: 'auto', background: theme.background, fontFamily: '"DM Sans", -apple-system, sans-serif' }}>
      <p style={{ padding: '10px 12px', margin: 0, fontSize: '12px', lineHeight: 1.45, color: theme.textSecondary }}>
        Score bases differ until gas-reference coverage is complete. “Reference” means the reference scenario is used in the score; gas delivery remains unverified. “Retained” means the existing score is retained because the gas reference is unassessed.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFeatureSettings: '"tnum"' }}>
        <thead>
          <tr style={{ position: 'sticky', top: 0, background: theme.surface, zIndex: 1 }}>
            <th
              onClick={() => handleSort('deal_quality')}
              style={{ ...thBase, color: sortField === 'deal_quality' ? designTokens.colors.brand : theme.textSecondary, borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
            >Deal Quality{sortIndicator('deal_quality')}</th>
            <th style={{ ...thBase, color: theme.textSecondary, borderBottom: `1px solid ${theme.border}` }}>Owner</th>
            <th style={{ ...thBase, color: theme.textSecondary, borderBottom: `1px solid ${theme.border}` }}>County</th>
            <th
              onClick={() => handleSort('dc_suitability_score')}
              style={{ ...thBase, color: sortField === 'dc_suitability_score' ? designTokens.colors.brand : theme.textSecondary, borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
            >DC Score{sortIndicator('dc_suitability_score')}</th>
            <th
              onClick={() => handleSort('acquisition_score')}
              style={{ ...thBase, color: sortField === 'acquisition_score' ? designTokens.colors.brand : theme.textSecondary, borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
            >Acq Score{sortIndicator('acquisition_score')}</th>
            <th
              onClick={() => handleSort('acreage')}
              style={{ ...thBase, color: sortField === 'acreage' ? designTokens.colors.brand : theme.textSecondary, borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
            >Acreage{sortIndicator('acreage')}</th>
            <th
              onClick={() => handleSort('total_appraised')}
              style={{ ...thBase, color: sortField === 'total_appraised' ? designTokens.colors.brand : theme.textSecondary, borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
            >Appraised{sortIndicator('total_appraised')}</th>
            <th style={{ ...thBase, color: theme.textSecondary, borderBottom: `1px solid ${theme.border}` }}>Absentee</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={colCount} style={{ padding: '20px', textAlign: 'center', color: theme.textSecondary }}>Loading...</td></tr>
          )}
          {!loading && parcels.length === 0 && (
            <tr><td colSpan={colCount} style={{ padding: '20px', textAlign: 'center', color: theme.textSecondary }}>No parcels found</td></tr>
          )}
          {sorted.map((p, idx) => {
            const dq = dealQuality(p);
            const dqColor = getDealColor(dq);
            const dcScore = p.dc_suitability_score == null ? null : Number(p.dc_suitability_score);
            const dcColor = dcScore == null ? theme.textTertiary : getDCScoreColor(dcScore);
            const reference = pipelineReferencePresentation(p);
            const stripeBg = idx % 2 === 1 ? `${theme.surface}40` : 'transparent';
            return (
              <tr
                key={p.id}
                onClick={() => onSelectParcel(p.id)}
                style={{ cursor: 'pointer', borderBottom: `1px solid ${theme.borderLight}`, color: theme.text, background: stripeBg }}
                onMouseOver={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                onMouseOut={e => (e.currentTarget.style.background = stripeBg)}
              >
                <td style={tdStyle}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '28px', height: '18px', borderRadius: '3px', fontSize: '10px', fontWeight: 700,
                    fontFamily: '"JetBrains Mono", monospace',
                    background: dqColor + '20', color: dqColor,
                  }}>
                    {dq}
                  </span>
                  <span style={{ fontSize: '8px', color: dqColor, marginLeft: '4px', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.04em' }}>
                    {getDealTier(dq)}
                  </span>
                </td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{p.owner_name}</td>
                <td style={tdStyle}>{p.county}, {p.state}</td>
                <td style={{ ...tdStyle, minWidth: 74 }} title={reference.scoreCopy}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '28px', height: '18px', borderRadius: '3px', fontSize: '10px', fontWeight: 700,
                    fontFamily: '"JetBrains Mono", monospace',
                    background: dcColor + '20', color: dcColor,
                  }}>
                    {formatDcScore(dcScore)}
                  </span>
                  <span style={{ display: 'block', marginTop: 2, fontSize: 8, lineHeight: 1, color: theme.textSecondary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {reference.kind === 'reference_scenario' ? 'Reference' : 'Retained'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '28px', height: '18px', borderRadius: '3px', fontSize: '10px', fontWeight: 700,
                    fontFamily: '"JetBrains Mono", monospace',
                    background: getScoreColor(p.acquisition_score, p.pipeline_available_mw) + '20',
                    color: getScoreColor(p.acquisition_score, p.pipeline_available_mw),
                  }}>
                    {p.acquisition_score}
                  </span>
                </td>
                <td style={{ ...tdStyle, fontFamily: designTokens.typography.fontFamily.mono, fontSize: '10px', fontWeight: 600 }}>
                  {p.acreage ? `${p.acreage}` : '\u2014'}
                </td>
                <td style={{ ...tdStyle, fontFamily: designTokens.typography.fontFamily.mono, fontSize: '10px' }}>
                  {p.total_appraised ? `$${Number(p.total_appraised).toLocaleString()}` : '\u2014'}
                </td>
                <td style={tdStyle}>
                  {p.is_absentee && (
                    <span style={{
                      fontSize: '8px', fontWeight: 700, padding: '2px 6px',
                      background: designTokens.colors.warningLight, color: designTokens.colors.warning,
                      borderRadius: '3px', fontFamily: 'monospace', letterSpacing: '0.04em',
                    }}>YES</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!loading && hasMore && (
        <div style={{ padding: '12px', textAlign: 'center', fontSize: '9px', color: theme.textTertiary, fontFamily: 'monospace' }}>
          Showing first {parcels.length.toLocaleString()} parcels — refine filters to narrow
        </div>
      )}
    </div>
  );
};

const thBase: React.CSSProperties = {
  padding: '6px 10px', textAlign: 'left', fontSize: '9px',
  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
  fontFamily: '"JetBrains Mono", monospace',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 10px', whiteSpace: 'nowrap',
};
